import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";

export const LeadForm = ({ pageSource = "unknown", defaultService = "", compact = false, simple = false }) => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error(t("common.errorMsg"));
      return;
    }
    if (!simple && !form.email) {
      toast.error(t("common.errorMsg"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, page_source: pageSource, language: lang };
      if (!payload.email) delete payload.email;
      await submitLead(payload);
      toast.success(t("common.successTitle"), { description: t("common.successMsg") });
      setForm({ name: "", email: "", phone: "", service: defaultService, message: "" });
    } catch {
      toast.error(t("common.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    { v: "facility-management", l: t("services.fm.title") },
    { v: "cleaning", l: t("services.cleaning.title") },
    { v: "maintenance", l: t("services.maintenance.title") },
    { v: "manpower", l: t("services.manpower.title") },
    { v: "amc", l: t("amc.sectionLabel") },
    { v: "other", l: t("contact.formMessage") },
  ];

  return (
    <form data-testid="lead-form" onSubmit={onSubmit} className={compact ? "space-y-4" : "space-y-5"}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {t("contact.formName")}
          </Label>
          <Input
            id="name"
            data-testid="lead-form-name"
            value={form.name}
            onChange={update("name")}
            required
            className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {t("contact.formPhone")}
          </Label>
          <Input
            id="phone"
            data-testid="lead-form-phone"
            value={form.phone}
            onChange={update("phone")}
            required
            placeholder="+971 ..."
            className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
      </div>

      {!simple && (
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {t("contact.formEmail")}
            <span className="ms-2 text-[10px] text-slate-400 normal-case tracking-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="lead-form-email"
            value={form.email}
            onChange={update("email")}
            required
            className="h-12 rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
          {t("contact.formService")}
        </Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger
            data-testid="lead-form-service"
            className="h-12 rounded-lg border-slate-300 focus:ring-blue-500"
          >
            <SelectValue placeholder={t("common.selectService")} />
          </SelectTrigger>
          <SelectContent>
            {services.map((s) => (
              <SelectItem key={s.v} value={s.v}>
                {s.l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!compact && !simple && (
        <div className="space-y-2">
          <Label htmlFor="message" className="text-xs uppercase tracking-[0.18em] text-slate-500">
            {t("contact.formMessage")}
          </Label>
          <Textarea
            id="message"
            data-testid="lead-form-message"
            value={form.message}
            onChange={update("message")}
            rows={4}
            className="rounded-lg border-slate-300 focus-visible:ring-blue-500"
          />
        </div>
      )}

      <button
        type="submit"
        data-testid="lead-form-submit"
        disabled={submitting}
        className="btn-primary-orion w-full disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("common.submitting")}
          </>
        ) : (
          <>
            {t("contact.submit")}
            <ArrowRight className="w-4 h-4 rtl-flip" />
          </>
        )}
      </button>
    </form>
  );
};
